import { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Modal from '@/components/base/Modal';
import { PLATFORM } from '@/config/platform';

/** The creators' app in the stores (PLATFORM.appLinks; Twiva's by default). */
const TWIVA_APP_LINKS = PLATFORM.appLinks;

type MobilePlatform = 'android' | 'ios' | null;

/**
 * Which phone or tablet this is, from the browser, not the window width: a narrow desktop window
 * is still desktop. iPadOS reports itself as a Mac, so a Mac with a touch screen counts as iOS.
 */
function mobilePlatform(): MobilePlatform {
  if (typeof navigator === 'undefined') return null;
  const agent = navigator.userAgent;
  if (/Android/i.test(agent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(agent)) return 'ios';
  if (/Macintosh/i.test(agent) && navigator.maxTouchPoints > 1) return 'ios';
  if (/Mobi|Mobile|Tablet|Silk|Kindle|Opera Mini|IEMobile/i.test(agent)) return 'android';
  return null;
}

const STORES = [
  { id: 'android', name: 'Google Play', device: 'Android', href: TWIVA_APP_LINKS.googlePlay, icon: 'ri-google-play-fill', phone: 'Android phone' },
  { id: 'ios', name: 'App Store', device: 'iPhone', href: TWIVA_APP_LINKS.appStore, icon: 'ri-apple-fill', phone: 'iPhone' },
] as const;
type Store = (typeof STORES)[number];

const iconClass =
  'store-icon flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600 text-2xl text-background-50 shadow-sm';

/** The whole tile (icon and its name) is the button, so tapping the words works too. */
const tileClass =
  'group flex w-24 cursor-pointer flex-col items-center gap-1.5 rounded-xl p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';

/** The icon with the store's name under it, for anyone who doesn't know the logos. */
function StoreTile({ store }: { store: Store }) {
  return (
    <>
      <span className={iconClass}>
        <i className={store.icon} aria-hidden="true" />
      </span>
      <span className="leading-tight">
        <span className="block text-xs font-semibold text-foreground-900 group-hover:text-primary-700">{store.name}</span>
        <span className="block text-[11px] text-foreground-500">{store.device}</span>
      </span>
    </>
  );
}

/**
 * "For creators · Get the Twiva app" with App Store and Google Play icons in burgundy, for the
 * creator-facing pages. On a phone or tablet an icon opens the store (the visitor's own store
 * first). On a computer, where the app can't be installed, it opens a card with a QR code to scan
 * with the phone instead, and a link to the store page if they'd still like to see it.
 */
export default function AppDownloadButtons({ className = '' }: { className?: string }) {
  const platform = useMemo(mobilePlatform, []);
  const [scanning, setScanning] = useState<Store | null>(null);
  const stores = [...STORES].sort((a, b) => Number(b.id === platform) - Number(a.id === platform));

  return (
    <section className={`animate-fade-rise text-center ${className}`} aria-label={`The ${PLATFORM.appName} for creators`}>
      {/* Clearly for creators: agencies use this portal, creators use the app. */}
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">For creators</p>
      <p className="mt-1 text-sm font-semibold text-foreground-900">Get the {PLATFORM.appName}</p>
      <div className="mt-3 flex items-start justify-center gap-4">
        {stores.map((store) =>
          platform ? (
            <a
              key={store.id}
              href={store.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Get the ${PLATFORM.appName} on ${store.name}`}
              title={store.name}
              className={tileClass}
            >
              <StoreTile store={store} />
            </a>
          ) : (
            <button
              key={store.id}
              type="button"
              onClick={() => setScanning(store)}
              aria-label={`Get the ${PLATFORM.appName} on ${store.name}`}
              title={`${store.name}: scan with your phone`}
              className={tileClass}
            >
              <StoreTile store={store} />
            </button>
          ),
        )}
      </div>

      <Modal
        open={scanning !== null}
        onClose={() => setScanning(null)}
        icon={scanning?.icon}
        title={`Get the ${PLATFORM.appName} on your phone`}
        subtitle={scanning ? `The app is for phones. Scan this code with your ${scanning.phone}'s camera to open ${scanning.name}.` : undefined}
        widthClassName="max-w-sm"
      >
        {scanning ? (
          <div className="flex flex-col items-center text-center">
            <div className="rounded-2xl border border-primary-100 bg-background-50 p-4 shadow-sm">
              <QRCodeSVG value={scanning.href} size={184} fgColor="#8A0E49" level="M" marginSize={0} />
            </div>
            <ol className="mt-4 space-y-1.5 text-left text-sm text-foreground-700">
              <li>1. Open the camera on your {scanning.phone}.</li>
              <li>2. Point it at the code and tap the link that appears.</li>
              <li>3. Install {PLATFORM.name} and sign in with the same account.</li>
            </ol>
            <div className="mt-4 flex flex-col items-center gap-2 text-sm">
              {STORES.filter((store) => store.id !== scanning.id).map((store) => (
                <button
                  key={store.id}
                  type="button"
                  onClick={() => setScanning(store)}
                  className="cursor-pointer font-medium text-primary-700 hover:text-primary-900"
                >
                  Using {store.id === 'ios' ? 'an iPhone' : 'Android'}? Show the {store.name} code
                </button>
              ))}
              <a href={scanning.href} target="_blank" rel="noopener noreferrer" className="text-foreground-500 underline-offset-2 hover:underline">
                Open {scanning.name} on this computer
              </a>
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
